/**
 * Aplicación del aspecto (color + forma) del CMDB.
 *
 * Es el único sitio que escribe los atributos de aspecto en <html>:
 *
 *   data-theme      → paleta de color          (`cmdb-theme`, clave histórica)
 *   data-skin       → skin de forma            (`cmdb-skin`)
 *   data-skin-base  → interruptor de la capa de forma de `skins.css`.
 *                     Solo existe si el skin impone su propio modo claro/oscuro.
 *   data-base       → modo efectivo (light/dark). Lo consume el bloque claro
 *                     heredado de `overrides.css`, que sigue siendo necesario
 *                     para las paletas claras.
 *
 * Preferencia del usuario: si hay skin guardado en el navegador, manda el suyo.
 * Si no hay nada guardado, se sigue el que la empresa tenga por defecto
 * (lo resuelve `App.jsx` al recibir la configuración de empresa).
 */

import { DEFAULT_PALETTE, getPalette, isValidPalette } from './palettes';
import { DEFAULT_SKIN, getSkin, isValidSkin } from './skins';
import { sincronizarColorDeBarra } from '../pwa';

export const PALETTE_STORAGE_KEY = 'cmdb-theme';
export const SKIN_STORAGE_KEY = 'cmdb-skin';
/** Último estilo resuelto de la empresa: evita el parpadeo mientras llega la API. */
export const COMPANY_SKIN_CACHE_KEY = 'cmdb-skin-company';

/**
 * Escribe el aspecto en <html>. Devuelve lo aplicado para poder mostrarlo.
 */
export function applyAppearance({ skin, palette } = {}) {
  const root = document.documentElement;
  const selectedSkin = getSkin(skin);
  const selectedPalette = getPalette(palette);

  root.setAttribute('data-theme', selectedPalette.id);
  root.setAttribute('data-skin', selectedSkin.id);

  // El modo lo decide el skin cuando lo impone (Lumina siempre es claro,
  // aunque la paleta elegida sea oscura); si no, lo decide la paleta.
  const base = selectedSkin.base || selectedPalette.base || 'dark';
  root.setAttribute('data-base', base);

  if (selectedSkin.base) {
    root.setAttribute('data-skin-base', selectedSkin.base);
  } else {
    root.removeAttribute('data-skin-base');
  }

  // La barra de estado del móvil (PWA) sigue al fondo de la paleta elegida.
  sincronizarColorDeBarra();

  return { skin: selectedSkin, palette: selectedPalette, base };
}

/** Lee lo que el usuario eligió en este navegador.
 *
 * `null` significa "no ha elegido": en el skin se hereda el de la empresa y en
 * la paleta se usa la de la empresa o la de fábrica. La distinción importa, por
 * eso no se devuelven valores por defecto aquí.
 */
export function readStoredAppearance() {
  let skin = null;
  let palette = null;
  try {
    const storedSkin = localStorage.getItem(SKIN_STORAGE_KEY);
    if (storedSkin && isValidSkin(storedSkin)) skin = storedSkin;
    const storedPalette = localStorage.getItem(PALETTE_STORAGE_KEY);
    if (storedPalette && isValidPalette(storedPalette)) palette = storedPalette;
  } catch {
    // Navegador con almacenamiento bloqueado: seguimos con los valores por defecto.
  }
  return { skin, palette };
}

/** Guarda el aspecto resuelto de la empresa para poder pintarlo al instante. */
export function cacheCompanyAppearance({ skin, palette }) {
  try {
    localStorage.setItem(
      COMPANY_SKIN_CACHE_KEY,
      JSON.stringify({ skin: skin || null, palette: palette || null })
    );
  } catch {
    // Sin caché: solo se pierde el arranque instantáneo, no la funcionalidad.
  }
}

function readCachedCompanyAppearance() {
  try {
    const raw = localStorage.getItem(COMPANY_SKIN_CACHE_KEY);
    if (!raw) return { skin: null, palette: null };
    const parsed = JSON.parse(raw);
    return {
      skin: isValidSkin(parsed?.skin) ? parsed.skin : null,
      palette: isValidPalette(parsed?.palette) ? parsed.palette : null,
    };
  } catch {
    return { skin: null, palette: null };
  }
}

/** Guarda la elección del usuario y la aplica.
 *
 * Un valor nulo BORRA la clave correspondiente: `skin: null` significa "seguir
 * el de mi empresa" y `palette: null` "usar el color que toque". Es importante
 * borrar en vez de ignorar, o una elección antigua seguiría ganando al recargar.
 */
export function saveAppearance({ skin, palette }) {
  try {
    if (skin) localStorage.setItem(SKIN_STORAGE_KEY, skin);
    else localStorage.removeItem(SKIN_STORAGE_KEY);
    if (palette) localStorage.setItem(PALETTE_STORAGE_KEY, palette);
    else localStorage.removeItem(PALETTE_STORAGE_KEY);
  } catch {
    // Sin persistencia, pero el aspecto se aplica igualmente en esta sesión.
  }
  return applyAppearance({ skin, palette });
}

/**
 * Aspecto inicial, antes de que React pinte nada (se llama desde `index.js`).
 *
 * Si el usuario no ha elegido skin en este navegador se usa el último skin de
 * empresa cacheado, para no ver primero Classic y luego el cambio cuando
 * responde la API. `App.jsx` lo confirma con el servidor al arrancar.
 */
export function bootAppearance() {
  const { skin, palette } = readStoredAppearance();
  const cached = readCachedCompanyAppearance();
  return applyAppearance({
    skin: skin || cached.skin,
    palette: palette || cached.palette,
  });
}

/**
 * Resuelve y aplica el aspecto definitivo: lo elegido en este navegador manda;
 * si no hay nada, se usa lo que devuelve el servidor (preferencia del usuario
 * en cualquier dispositivo y aspecto por defecto de la empresa).
 */
export function resolveAppearance(serverTheme) {
  const local = readStoredAppearance();
  const effective = serverTheme?.effective || {};
  const applied = applyAppearance({
    skin: local.skin || effective.skin,
    palette: local.palette || effective.palette,
  });
  if (serverTheme?.company) cacheCompanyAppearance(serverTheme.company);
  return applied;
}

export const DEFAULT_APPEARANCE = { skin: DEFAULT_SKIN, palette: DEFAULT_PALETTE };
